<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5"> 
      <TableTitle
        :title="title"
        pageTitle="Levels"
        icon="fas fa-layer-group"
        :showAction="true"
        actionLabel="Create New"
        modal="createNew"
        :disabled="!canCreate('agents')"
      />
      <CommonTable2
        :tableObj="tableConfig" 
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="agents"
      />
    </div>
  </div>

  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 px-3">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      >
        <template #additional-fields>
          <div class="mb-3">
            <label class="form-label">Knowledge Bases</label>
            <VueMultiselect
              v-model="newItemData.knowledge_base_ids"
              :options="knowledgeBases"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Select Knowledge Bases"
              label="name"
              track-by="_id"
              :preselect-first="false"
            />
          </div>
        </template>
      </BaseForm>
    </template>
  </BaseModal>

  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      >
        <template #additional-fields>
          <div class="mb-3">
            <label class="form-label">Knowledge Bases</label>
            <VueMultiselect
              v-model="editingItemData.knowledge_base_ids"
              :options="knowledgeBases"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Select Knowledge Bases"
              label="name"
              track-by="_id"
              :preselect-first="false"
              :disabled="isViewMode"
            />
          </div>
        </template>
      </BaseForm>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue"; 
import { usePermissions } from "@/composables/usePermissions";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import apiService from "@/services/apiService";
import { toast, type Content } from "vue3-toastify";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

const { canCreate } = usePermissions();
const API_RESOURCE = "cms-agent-meta/levels";
const pageTitle = "Level";
const title = "Agent Levels";

const crud = useCrud(API_RESOURCE);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [1];
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

const rows = ref<any[]>([]);
const tableConfig = ref({
 columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description", sortable: true, width: "250px" },
    { name: "Knowledge Bases", field: "kb_count", sortable: true,  type: "badge" }
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",  
});

watch(crud.data, (newVal) => {
  rows.value = (newVal || []).map((item: any) => ({
    ...item,
    kb_count: (item.knowledge_base_ids || []).length
  }));
});
watch(crud.isLoading, (v) => tableConfig.value.loading = v);
watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

const changeRows = () => {
  currentPage.value = 1;
  crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};
const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) return;
  currentPage.value = page;
  crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);
const newItemData = ref({ title: "", description: "", knowledge_base_ids: [] });
const editingItemData = ref<any>({ _id: "", title: "", description: "", knowledge_base_ids: [] });

const knowledgeBases = ref<any[]>([]);

const loadKnowledgeBases = async () => {
  try {
    const response = await apiService.getKnowledgeBases({ isActive: true });
    if (response) {
      knowledgeBases.value = response.data || response.knowledgeBases || [];
    }
  } catch (err: any) {
    console.error("Error loading knowledge bases:", err);
  }
};

const createFormFields: FormField[] = [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { key: "description", label: "Description", type: "textarea", placeholder: "Enter description" },
];
const editFormFields = computed<FormField[]>(() => [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title", readonly: isViewMode.value },
  { key: "description", label: "Description", type: "textarea", placeholder: "Enter description", readonly: isViewMode.value },
]);

const isCreateFormValid = computed(() => !newItemData.value.title.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { 
    ...row,
    knowledge_base_ids: (row.knowledge_base_ids || []).map((kb: any) => {
      const id = typeof kb === 'object' ? (kb._id || kb.id) : kb;
      const found = knowledgeBases.value.find(k => (k._id || k.id) === id);
      return found || kb;
    })
  };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { 
    ...row,
    knowledge_base_ids: (row.knowledge_base_ids || []).map((kb: any) => {
      const id = typeof kb === 'object' ? (kb._id || kb.id) : kb;
      const found = knowledgeBases.value.find(k => (k._id || k.id) === id);
      return found || kb;
    })
  };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};
const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const payload = {
    ...newItemData.value,
    knowledge_base_ids: newItemData.value.knowledge_base_ids.map((kb: any) => typeof kb === 'object' ? (kb._id || kb.id) : kb)
  };
  const res = await crud.createItem(payload);
  if (res) {
    isCreateModalOpen.value = false;
    newItemData.value = { title: "", description: "", knowledge_base_ids: [] };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};
const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...rest } = editingItemData.value;
  const payload = {
    ...rest,
    knowledge_base_ids: rest.knowledge_base_ids.map((kb: any) => typeof kb === 'object' ? (kb._id || kb.id) : kb)
  };
  try {
    crud.isLoading.value = true;
    const res = await apiService.put(API_RESOURCE, id, payload);
    if (res) {
      toast.success("Item updated successfully" as Content, { theme: "colored" });
      isEditModalOpen.value = false;
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error((err.response?.data?.message || err.message || "Failed to update.") as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};
const closeCreateModal = () => { isCreateModalOpen.value = false; newItemData.value = { title: "", description: "", knowledge_base_ids: [] }; };
const closeEditModal = () => { isEditModalOpen.value = false; isViewMode.value = false; editingItemData.value = { _id: "", title: "", description: "", knowledge_base_ids: [] }; };

onMounted(() => {
  loadKnowledgeBases();
  crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
});
</script>

<style scoped>
.rows-select { width: 82px; padding: 8px 17px; font-size: 0.85rem; background-color: transparent; }
.pagination-mini { display: flex; align-items: center; border-radius: 6px; overflow: hidden; }
.page-btn { border: none; background: transparent; font-size: 1rem; padding: 7px 12px; cursor: pointer; color: #6c757d; transition: 0.2s; border: 1px solid #dee2e6; outline: none; }
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; padding: 7px 12px; font-weight: 600; min-width: 28px; text-align: center; display: inline-block; border: 1px solid #21c1d6; }
.prev_btn, .next_btn { color: #21c1d6; }

/* VueMultiselect Premium Styling */
:deep(.multiselect__tags) {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 8px 40px 0 8px;
}

:deep(.multiselect__tag) {
  background: #35bfbf;
}

:deep(.multiselect__tag-icon:after) {
  color: #fff;
}

:deep(.multiselect__tag-icon:focus, .multiselect__tag-icon:hover) {
  background: #2a9b9b;
}

:deep(.multiselect__option--highlight) {
  background: #35bfbf;
}

:deep(.multiselect__option--highlight:after) {
  background: #35bfbf;
}
</style>
